<template>
    <v-container>
        <v-card :loading="loading">
            <v-card-title :style="{ 'background-color': '#eeeeee' }">
                <div>
                    LOUVER SIZING TABLES<br />
                    <span
                        class="text-body-2"
                        style="text-align: left !important;"
                    >
                        Double click to edit
                    </span>
                </div>
            </v-card-title>
            <v-tabs v-model="activeModel" background-color="transparent">
                <v-tab v-for="model in models" :key="model" :href="`#${model}`">
                    {{ model }}
                </v-tab>
            </v-tabs>
            <v-tabs-items v-model="activeModel">
                <v-tab-item v-for="model in models" :key="model" :value="model">
                    <div class="pa-4">
                        <h4 class="mb-2">By Dimension</h4>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th class="text-center">SIZE</th>
                                        <th class="text-center">
                                            DIM MIN (in)
                                        </th>
                                        <th class="text-center">
                                            DIM MAX (in)
                                        </th>
                                        <th class="text-center">
                                            DIM MIN (mm)
                                        </th>
                                        <th class="text-center">
                                            DIM MAX (mm)
                                        </th>
                                        <th class="text-center">
                                            AREA MIN (m2)
                                        </th>
                                        <th class="text-center">
                                            AREA MAX (m2)
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(row, index) in (
                                            modelData[model] || {}
                                        ).byDimension || []"
                                        :key="`dim-${model}-${index}`"
                                    >
                                        <td class="text-center">
                                            {{ row.size }}
                                        </td>
                                        <td class="text-center">
                                            <v-text-field
                                                v-model.number="row.dimMinIn"
                                                type="number"
                                                dense
                                                outlined
                                                hide-details
                                                class="sizing-input"
                                                @blur="onDimensionBlur(row)"
                                                @keydown.enter="
                                                    onDimensionBlur(row)
                                                "
                                            />
                                        </td>
                                        <td class="text-center">
                                            <v-text-field
                                                v-model.number="row.dimMaxIn"
                                                type="number"
                                                dense
                                                outlined
                                                hide-details
                                                class="sizing-input"
                                                @blur="onDimensionBlur(row)"
                                                @keydown.enter="
                                                    onDimensionBlur(row)
                                                "
                                            />
                                        </td>
                                        <td class="text-center">
                                            {{ formatNumber(row.dimMinMm) }}
                                        </td>
                                        <td class="text-center">
                                            {{ formatNumber(row.dimMaxMm) }}
                                        </td>
                                        <td class="text-center">
                                            <v-text-field
                                                v-model.number="row.areaMinM2"
                                                type="number"
                                                dense
                                                outlined
                                                hide-details
                                                class="sizing-input"
                                                @blur="persistCurrentModel"
                                                @keydown.enter="
                                                    persistCurrentModel
                                                "
                                            />
                                        </td>
                                        <td class="text-center">
                                            <v-text-field
                                                v-model.number="row.areaMaxM2"
                                                type="number"
                                                dense
                                                outlined
                                                hide-details
                                                class="sizing-input"
                                                @blur="persistCurrentModel"
                                                @keydown.enter="
                                                    persistCurrentModel
                                                "
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                        <h4 class="mb-2 mt-6">By Weight</h4>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th class="text-center">SIZE</th>
                                        <th class="text-center">
                                            PESO MIN (kg)
                                        </th>
                                        <th class="text-center">
                                            PESO MAX (kg)
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(row, index) in (
                                            modelData[model] || {}
                                        ).byWeight || []"
                                        :key="`weight-${model}-${index}`"
                                    >
                                        <td class="text-center">
                                            {{ row.size }}
                                        </td>
                                        <td class="text-center">
                                            <v-text-field
                                                v-model.number="row.weightMinKg"
                                                type="number"
                                                dense
                                                outlined
                                                hide-details
                                                class="sizing-input"
                                                @blur="persistCurrentModel"
                                                @keydown.enter="
                                                    persistCurrentModel
                                                "
                                            />
                                        </td>
                                        <td class="text-center">
                                            <v-text-field
                                                v-model.number="row.weightMaxKg"
                                                type="number"
                                                dense
                                                outlined
                                                hide-details
                                                class="sizing-input"
                                                @blur="persistCurrentModel"
                                                @keydown.enter="
                                                    persistCurrentModel
                                                "
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </div>
                </v-tab-item>
            </v-tabs-items>
        </v-card>
    </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'

const MODELS = [
    '5LS-H',
    '5LS-V',
    '5LS-HV',
    '1.8LS-H',
    '1.4LS-42',
    '1.4LS-60',
    '3LS-V',
]

export default {
    name: 'LouverSizingTable',
    props: {
        settingId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            models: MODELS,
            activeModel: '5LS-H',
            modelData: {},
        }
    },
    async mounted() {
        try {
            await this.getItems()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        inchesToMm(inches) {
            return Number(inches) * 25.4
        },
        normalizeDimensionRow(row) {
            const next = { ...row }
            if (
                (next.areaMinM2 == null || next.areaMinM2 === '') &&
                next.areaThresholdM2 != null
            ) {
                next.areaMinM2 = next.areaThresholdM2
            }
            if (next.areaMaxM2 == null || next.areaMaxM2 === '') {
                next.areaMaxM2 = 0
            }
            return next
        },
        recalculateDimension(row) {
            row.dimMinMm = Number(this.inchesToMm(row.dimMinIn).toFixed(2))
            row.dimMaxMm = Number(this.inchesToMm(row.dimMaxIn).toFixed(2))
        },
        formatNumber(value) {
            if (value == null || value === '') return '--'
            const n = Number(value)
            return Number.isNaN(n) ? '--' : n
        },
        async persistCurrentModel() {
            try {
                const current = this.modelData[this.activeModel]
                if (!current) return
                this.loading = true
                for (const row of current.byDimension) {
                    this.recalculateDimension(row)
                }
                await API.updateLouverSizingTable({
                    settingId: this.settingId,
                    model: this.activeModel,
                    byDimension: current.byDimension,
                    byWeight: current.byWeight,
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async onDimensionBlur(row) {
            this.recalculateDimension(row)
            await this.persistCurrentModel()
        },
        async getItems() {
            try {
                this.loading = true
                let items = await API.getLouverSizingTable({
                    settingId: this.settingId,
                })
                if (!items || items.length === 0) {
                    items = await API.createLouverSizingTable({
                        settingId: this.settingId,
                    })
                }
                const map = {}
                for (const item of items) {
                    map[item.model || item.id] = {
                        model: item.model || item.id,
                        byDimension: (item.byDimension || []).map(row =>
                            this.normalizeDimensionRow(row)
                        ),
                        byWeight: (item.byWeight || []).map(row => ({
                            ...row,
                        })),
                    }
                }
                for (const model of this.models) {
                    if (!map[model]) {
                        map[model] = {
                            model,
                            byDimension: [],
                            byWeight: [],
                        }
                    }
                }
                this.modelData = map
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped>
.sizing-input {
    max-width: 110px;
    margin: 0 auto;
}
</style>
